import {observer} from "mobx-react";
import {
    Container,
    Content,
    ContentFormView,
    ContentView
} from "@dropDesk/presentation/components/internal_chat_view/styled";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {ImageEditorController} from "@dropDesk/presentation/pages/image_editor/controller/image_editor.controller";
import DropDeskLoading from "@dropDesk/presentation/components/loadings/loading_dropdesk";
import React, {useEffect, useRef} from "react";
import {InternalChatController} from "@dropDesk/presentation/pages/chat/controller/internal_chat.controller";
import {Watermark} from "@dropDesk/presentation/pages/ticket/ui/styled";
import {Brightness} from "@dropDesk/domain/entities/theme/colors.entity";
import GenericScreenNotFound from "@dropDesk/presentation/components/generic_screen_not_found";
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import HeaderViewInformation from "@dropDesk/presentation/components/internal_chat_view/header_view_information";
import {useBetween} from "use-between";
import {useModalChat} from "@dropDesk/presentation/pages/chat/ui/modals/visible_modal_search";
import HeaderActionButtons from "@dropDesk/presentation/components/internal_chat_view/header_action_buttons";
import { ChatController} from "@dropDesk/presentation/pages/chat/controller/chat.controller";
import {CancelTokenManager} from "@dropDesk/data/clients/http.client";
import ImageEditor from "@dropDesk/presentation/pages/image_editor/ui";
import {FileEditionEntity} from "@dropDesk/domain/entities/image_editor/image_editor.entity";
import ChatMessage, {ChildRefType} from "@dropDesk/presentation/components/chat_view/chat_message";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {t} from "i18next";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";
import {ChatMessageEntity} from "@dropDesk/domain/entities/messages/message_enums";
import {AudioRecordEntity} from "@dropDesk/domain/entities/common/audio_record.entity";
import {newInternalChatMessage} from "@dropDesk/presentation/pages/chat/controller/new_internal_chat_message";

const InternalChatView = observer((
    {
        internalChatId,
        widthContainerListChats
    }: {
        internalChatId?: string
        widthContainerListChats?: number
    }
) => {

    const imageEditorController = useInjection(ImageEditorController);
    const internalChatController = useInjection(InternalChatController);
    const chatController = useInjection(ChatController);
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const userLogged = appController.user!;
    const fixHeight: number = imageEditorController.filesEdition.length === 0 ? 45 + appController.heightShowingHeaderInfo : 0 + appController.heightShowingHeaderInfo;
    const currentInternalChat: InternalChatEntity | undefined | null = internalChatController.currentInternalChat;
    const childRef = useRef<ChildRefType | null>(null);

    const {
        setVisibleModalNewInternalChat,
        setVisibleModalDeleteInternalChat
    } = useBetween(useModalChat);

    const previousInternalChatIdRef = useRef<string | undefined>();

    const handleClickEditDescription = (currentInternalChat: InternalChatEntity) => {
        internalChatController.makeInternalChat(currentInternalChat);
        setVisibleModalNewInternalChat(true);
    }

    useEffect(() => {
        const previousInternalChatId = previousInternalChatIdRef.current;
        const hasChanged = previousInternalChatId !== internalChatId;

        if (hasChanged && internalChatId) {
            initialize(internalChatId);
        }
        previousInternalChatIdRef.current = internalChatId;
    }, [internalChatId]);

    const initialize = async (idInternalChat: string): Promise<void> => {
        try {
            const cancelTokenSource = CancelTokenManager.createNewToken();
            imageEditorController.setIdTicket(idInternalChat);
            await internalChatController.loadStaticMessages(cancelTokenSource);
            await internalChatController.informReadMessagesIfNeeded(internalChatController.eligibleMessagesToRead(), cancelTokenSource);
        } catch (error) {
            console.error(error);
        } finally {
            CancelTokenManager.clearToken();
        }
    }

    const onSaveFileEditor = async (currentInternalChat: InternalChatEntity, filesEdition: FileEditionEntity[]): Promise<void> => {
        const currentMessage = childRef.current?.getCurrentMessage();
        const filesSave = filesEdition.map((entry) => entry.fileMessage);
        const _filesMessage = [...filesSave];
        const messages: InternalChatMessageEntity[] = internalChatController.parseFileToMessage(currentInternalChat, _filesMessage, userLogged);
        messages[0].reply = currentMessage?.reply;
        internalChatController.handleSendMessages(messages, currentInternalChat, userLogged);
    }

    const sendAudio = async (message: ChatMessageEntity, audioRecorder: AudioRecordEntity) => {
        const _message = await internalChatController.parseAudioToMessage(
            currentInternalChat!,
            audioRecorder,
            userLogged,
            message as InternalChatMessageEntity
        );
        internalChatController.handleSendMessages([_message], currentInternalChat!, userLogged);
    }

    const showOriginalFile = (message: ChatMessageEntity, currentInternalChat: InternalChatEntity) => {
        const newMessage = InternalChatMessageEntity.fromJson({
            ...message,
            showOriginal: true,
        });
        internalChatController.updateMessage(currentInternalChat, [newMessage]);
    }

    const setLoaded = (message: ChatMessageEntity, currentInternalChat: InternalChatEntity) => {
        const newMessage = InternalChatMessageEntity.fromJson({
            ...message,
            loaded: true,
        });
        internalChatController.updateMessage(currentInternalChat, [newMessage]);
    }

    const createNewMessage = (currentInternalChat: InternalChatEntity): InternalChatMessageEntity => {
        return newInternalChatMessage(currentInternalChat.id);
    }


    const onSuccess = (key?: string) => {
        if (key) {
            toastMessage.success(t(key));
        }

        // sleep(.5).then(() => {
        //     childRef.current?.setInputFocus();
        // })

    }


    return (
        <Container background={colors.backgroundChatBox} fixHeight={fixHeight}>
            <Content>
                <ContentFormView>
                    <ContentView background={colors.backgroundChatBox}>
                        <Watermark
                            isDarkTheme={colors.brightness === Brightness.dark}
                            background={colors.backgroundChatBox}
                        >

                            {(!internalChatController.internalChatNotFound && currentInternalChat) &&
                                <>
                                    <HeaderViewInformation
                                        internalChat={currentInternalChat}
                                        canChangeDescription={currentInternalChat.isCreator(userLogged)}
                                        widthContainerListChats={widthContainerListChats}
                                        onClickEditDescription={() => handleClickEditDescription(currentInternalChat)}
                                    />
                                    <HeaderActionButtons
                                        compactModeMessages={chatController.compactModeMessages}
                                        setCompactModeMessage={(value) => chatController.setCompactModeMessage(value)}
                                        canChange={currentInternalChat.hasEligibleInternalChat(userLogged)}
                                        canChangeDelete={currentInternalChat.isCreator(userLogged)}
                                        onClickDelete={() => setVisibleModalDeleteInternalChat(true)}
                                    />
                                    <ChatMessage
                                        handlePickFile={(files) => imageEditorController.handlePickFile(files, false)}
                                        handleOnScrollTop={() => internalChatController.loadMoreMessages('top')}
                                        handleOnScrollBottom={() => internalChatController.loadMoreMessages('bottom')}
                                        canChange={currentInternalChat.hasEligibleInternalChat(userLogged)}
                                        canSendMessage={currentInternalChat.hasEligibleInternalChat(userLogged)}
                                        forcePrivateComment={false}
                                        userLogged={userLogged}
                                        compactModeMessages={chatController.compactModeMessages}
                                        userClient={null}
                                        messages={internalChatController.messages}
                                        idMessageFocused={internalChatController.idMessageFocused}
                                        idParent={currentInternalChat.id}
                                        handleClickOnMessageReply={(reply) => internalChatController.handleClickOnMessageReply(reply)}
                                        cancelUpload={(upload) => {
                                            upload?.cancel();
                                        }}
                                        targetMessagePaginationTop={internalChatController.targetMessagePaginationTop}
                                        targetMessagePaginationBottom={internalChatController.targetMessagePaginationBottom}
                                        targetMessageButtonNavigateBottom={internalChatController.targetMessageButtonNavigateBottom}
                                        targetVirtualizedMessage={internalChatController.targetVirtualizedMessage}
                                        handleScrollTopVirtualizedMessage={() => internalChatController.handleScrollTopVirtualizedMessage()}
                                        handleMoveCursorToFinal={() => internalChatController.handleMoveCursorToFinal()}
                                        onRetrySendMessageError={(message) => internalChatController.retrySendMessage(message as InternalChatMessageEntity, currentInternalChat, userLogged)}
                                        bottomMessageLoading={((internalChatController.currentInternalChatMessages.data.length === 0 || internalChatController.scrollPosition === 'bottom') && internalChatController.loading)}
                                        topMessageLoading={internalChatController.currentInternalChatMessages.data.length > 0 && internalChatController.scrollPosition !== 'bottom' && internalChatController.loading}
                                        sendMessage={(message) => internalChatController.handleSendMessages([message as InternalChatMessageEntity], currentInternalChat, userLogged)}
                                        handleRemoveReaction={(reaction, messageReacted) => internalChatController.removeReaction(messageReacted as InternalChatMessageEntity, reaction as InternalChatMessageEntity, currentInternalChat)}
                                        handleAddReaction={(reaction, messageToReacted) => internalChatController.handleAddReaction(messageToReacted as InternalChatMessageEntity, reaction, userLogged, currentInternalChat)}
                                        lastUsedReactions={internalChatController.lastUsedReactions}
                                        sendAudio={(message, audioRecorder) => sendAudio(message, audioRecorder)}
                                        handleEditMessage={(ediText, currentMessage) => internalChatController.editMessage(ediText, currentMessage as InternalChatMessageEntity, currentInternalChat)}
                                        handleDeleteMessage={(message) => internalChatController.deleteMessage(currentInternalChat, message as InternalChatMessageEntity)}
                                        handleResetMessageFocused={() => internalChatController.handleResetMessageFocused()}
                                        allReactions={internalChatController.reactions}
                                        attendant={undefined}
                                        stopLoading={() => internalChatController.stopLoading()}
                                        isMeAttendingTicket={currentInternalChat.hasEligibleInternalChat(userLogged)}
                                        ticketIsOpen={true}
                                        childRef={childRef}
                                        canEditFile={currentInternalChat.hasEligibleInternalChat(userLogged)}
                                        showOriginalFile={(message) => showOriginalFile(message, currentInternalChat)}
                                        setLoaded={(message) => setLoaded(message, currentInternalChat)}
                                        createNewMessage={() => createNewMessage(currentInternalChat)}
                                        startingServiceInternalChatHeaderText={currentInternalChat.startingServiceInternalChatHeaderText}
                                        closingServiceInternalChatHeaderText={currentInternalChat.closingServiceInternalChatHeaderText}
                                    />

                                    <ImageEditor
                                        onSave={async (referenceImageEdited) => {
                                            if (!imageEditorController.loading) {
                                                imageEditorController.startLoading('imageEditor.loadingSave');
                                                imageEditorController.setLoadableDesignState(referenceImageEdited);
                                                const filesEdition = await imageEditorController.save();
                                                onSaveFileEditor(currentInternalChat, filesEdition);
                                            }
                                        }}
                                        hasActiveStatus={currentInternalChat.hasActiveStatus}
                                        imageEditorController={imageEditorController}
                                        id={currentInternalChat.id}
                                        widthContainerListChats={widthContainerListChats}
                                        forcePrivateComment={false}
                                        usePrivateComment={false}
                                    />
                                </>
                            }

                            {
                                internalChatController.internalChatNotFound &&
                                <GenericScreenNotFound
                                    textNotFound={'Oops, Chat interno não encontrado ...'}
                                    textHeaderForm={"Voltar"}
                                />
                            }
                        </Watermark>
                    </ContentView>

                </ContentFormView>
            </Content>
            {
                (internalChatController.loading && internalChatController.loadingMessage) && <DropDeskLoading
                    height={250}
                    description={internalChatController.loadingMessage}
                />
            }
        </Container>
    )
});

export default InternalChatView;